import React from 'react';
import maxBy from 'lodash/maxBy';
import { Buffer } from 'buffer';
import CSV from 'comma-separated-values';
import { Box, Checkbox, styled, Typography } from '@mui/material';
import { DataGridPremium, type GridColDef } from '@mui/x-data-grid-premium';

import { type FilesViewerProps } from '../FilesViewer';
import { renderCellWithTooltip } from '../../page-layouts/view/table/ViewTableUtils';

const StyledDataGrid = styled(DataGridPremium)(({ theme }) => ({
	'.MuiDataGrid-cell': {
		display: 'flex',
		alignItems: 'center',
		fontVariantNumeric: 'tabular-nums'
	},
	'.MuiDataGrid-columnHeader.CsvViewer-headerCheckbox': {
		paddingLeft: 6,
		paddingRight: 6
	}
}));

/** This component can render a comma-separated file (CSV) */
export default function CsvViewer({ fileEncoded }: FilesViewerProps) {
	const [csvRows, setCsvRows] = React.useState<any[]>([]);

	const [showHeaderRow, setShowHeaderRow] = React.useState(true);

	React.useEffect(() => {
		const decoded = Buffer.from(fileEncoded, 'base64');
		const csv = CSV.parse(decoded.toString());

		setCsvRows(csv);
	}, [fileEncoded]);

	const rows: any[] = [];
	const columns: GridColDef[] = [
		{
			field: '0',
			headerName: '',
			width: 50,
			headerClassName: 'CsvViewer-headerCheckbox',
			renderHeader: () => (
				<Checkbox size="small" checked={showHeaderRow} onChange={e => setShowHeaderRow(e.target.checked)} />
			),
			renderCell: ({ value }) => (
				<Typography className="w-full text-center" variant="subtitle2">
					{value}
				</Typography>
			),
			sortable: false
		}
	];

	const headerRow: any[] = csvRows[0];

	csvRows
		?.filter((_, index) => (showHeaderRow && index !== 0) || !showHeaderRow)
		.forEach((array, rowIndex) => {
			const fields = Object.values(array);

			if (rowIndex === 0) {
				const rowLength = maxBy(csvRows, (row: any[]) => row.length)?.length;

				for (let index = 0; index < (rowLength || 0); index += 1)
					columns.push({
						field: `${index + 1}`,
						headerName: String.fromCharCode('A'.charCodeAt(0) + index),
						minWidth: 150,
						flex: 1,
						renderHeader: ({ colDef }) => (
							<Typography variant="subtitle2">
								{showHeaderRow ? headerRow[index] : colDef.headerName}
							</Typography>
						),
						renderCell: renderCellWithTooltip
					});
			}

			const row = { id: rowIndex, 0: rowIndex + 1 };
			fields.forEach((cell, index) => {
				row[index + 1] = cell;
			});
			rows.push(row);
		});

	return (
		<Box className="h-full w-full">
			<StyledDataGrid
				columns={columns}
				rows={rows}
				density="compact"
				disableColumnMenu
				disableRowSelectionOnClick
				showColumnVerticalBorder
				showCellVerticalBorder
				hideFooter
			/>
		</Box>
	);
}
